import { useEffect, useState } from 'react'
import { ActionRequest } from '../types'
import { AxiosRequestConfig } from 'axios'
import { getRequests } from '../services/requestService'
import ActionRequestDataGrid from './ActionRequestDataGrid'
import { Priority } from '../enums'
import { RequestStatus } from '../enums/RequestStatus'
import { Card, CardContent, CardHeader, Stack } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { GridPaginationModel, GridSlots } from '@mui/x-data-grid'
import { usePolicy } from '../hooks'

export default function ActionRequestDashboard() {
  /**
   * The loading state.
   */
  const [loading, setLoading] = useState<boolean>(false)

  /**
   * The action requests state.
   */
  const [actionRequests, setActionRequests] = useState<ActionRequest[]>([])

  /**
   * The error state.
   */
  const [error, setError] = useState<unknown>(null)

  /**
   * The translate function.
   */
  const { t } = useTranslation('common')

  const { isSuperadmin, canUpdateActionRequestState } = usePolicy()

  /**
   * The pagination model.
   */
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    pageSize: 25,
    page: 0
  })

  /**
   * Load the action requests.
   */
  async function loadActionRequests(config?: AxiosRequestConfig) {
    try {
      setLoading(true)
      setError(undefined)

      // Get the action requests for the project.
      const actionRequests = await getRequests(
        {
          status: RequestStatus.REQUESTED,
          priority: [Priority.CRITICAL, Priority.HIGH]
        },
        config
      )

      setActionRequests(
        actionRequests.filter((actionRequest) => canUpdateActionRequestState(actionRequest))
      )
    } catch (error) {
      setError(error)
    } finally {
      setLoading(false)
    }
  }

  /**
   * Remove action request.
   */
  function removeActionRequest(actionRequest: ActionRequest) {
    const newActionRequests = actionRequests.filter((a) => a.id !== actionRequest.id)
    setActionRequests(newActionRequests)
  }

  /**
   * Load action requests when the query changes.
   */
  useEffect(() => {
    const controller = new AbortController()
    loadActionRequests({ signal: controller.signal })
    return () => controller.abort()
  }, [])

  return !loading && actionRequests.length > 0 && !isSuperadmin ? (
    <Stack direction="column" spacing={2} sx={{ mb: 6 }}>
      <Card>
        <CardHeader title={t('actionRequests')} />
        <CardContent>
          <ActionRequestDataGrid
            slots={
              {
                toolbar: null
              } as GridSlots
            }
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            actionRequests={actionRequests}
            afterAccept={removeActionRequest}
            afterReject={removeActionRequest}
          />
        </CardContent>
      </Card>
    </Stack>
  ) : null
}
